import { getTenantInfo } from "@/helpers/apis";
import { setFreemiumMode } from "./freemiumMode";
import { getDefaultRoute, getTenant, isFreeStack, isTenantlessEnterpriseHost } from "./tenant";

/**
 * Resolve where to send a user immediately after they authenticate.
 *
 * The tenant-level freemium flag is fetched by AppProvider's effect, which runs
 * only *after* the render that follows `setLoggedinUser` — and on the login
 * transition `loadingTenantInfo` is already `false`, because it was set false so
 * the login page itself could render. So the router resolves the new location
 * against the pre-fetch default, the enterprise catch-all sends the user to
 * `/summary`, and nothing moves them off that explicit route once the flag
 * arrives. Navigating to "/" does not defer the decision far enough; the only
 * fix is to know the answer before navigating.
 *
 * Reloads were never affected — there `loadingTenantInfo` starts true and the
 * provider gates every child until the fetch resolves. This is specific to the
 * in-session transition.
 *
 * A failed fetch is not fatal: fall back to hostname-derived behaviour, which is
 * correct for every tenant except a migrated freemium one.
 */
export const loadFreemiumMode = async (): Promise<void> => {
  // The public-admin host has no tenant schema, so the tenant-scoped
  // /auth/tenant-info must not be sent from it — and freemium mode is a
  // per-tenant flag, so there is nothing to load there anyway. The free-stack
  // carve-out (the bare free host keeps its fetch) lives in the predicate.
  if (isTenantlessEnterpriseHost()) return;
  try {
    const tenantInfo = await getTenantInfo();
    setFreemiumMode(!!tenantInfo?.freemiumModeEnabled);
  } catch {
    // Keep the hostname-derived default rather than blocking the login.
  }
};

export const resolvePostLoginRoute = async (): Promise<string> => {
  await loadFreemiumMode();
  // In freemium mode "/" mounts Quickstart; otherwise the caller's default.
  return isFreeStack() ? "/" : getDefaultRoute(getTenant());
};

/**
 * Is `from` a destination the user actually chose, or an artefact of the
 * pre-login redirect?
 *
 * The auth guard stashes wherever the router had already resolved to. For a
 * logged-out visit to the app root that is the enterprise default route, so
 * honouring it would send a freemium-mode tenant to /summary — the exact bug
 * this module exists to prevent. A genuine deep link to any other path is still
 * honoured.
 */
export const isRedirectArtifact = (from: string | undefined): boolean =>
  !!from && isFreeStack() && (from === "/" || from === getDefaultRoute(getTenant()));

/**
 * sessionStorage key for deep-link return after workspace login.
 *
 * AppProvider unmounts/remounts Login during its loading cycle (see comment in
 * Login.tsx onSuccess). React Router's `state.from` can be lost across that
 * remount. This sessionStorage stash is the robust alternative: it survives
 * component unmounts and is cleared on pop so it never hijacks a later login.
 */
const WS_DEEP_LINK_KEY = "ws_deep_link_return";

/** Validate that a path is a safe same-app relative path (no open redirect). */
function isAllowedDeepLinkPath(path: string): boolean {
  if (typeof path !== "string" || path === "") return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  // Reject paths that only resolve to login — that would be an infinite loop.
  if (path.startsWith("/login")) return false;
  try {
    const u = new URL(path, window.location.origin);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Stash a workspace deep-link path in sessionStorage so it survives the
 * AppProvider loading cycle that unmounts/remounts Login.tsx.
 * Only stashes non-trivial paths (skips "/" and "/login").
 */
export const stashDeepLinkReturn = (from: string): void => {
  if (!isAllowedDeepLinkPath(from) || from === "/" || isRedirectArtifact(from)) return;
  try {
    sessionStorage.setItem(WS_DEEP_LINK_KEY, from);
  } catch {
    // Private mode / storage full — fall back to state.from.
  }
};

/**
 * Drop the stashed deep-link path without reading it.
 *
 * Logout calls this. The stash is written by the auth guard and read by exactly one
 * place — `Login.onSuccess` — so any sign-in that completes elsewhere (`SocialComplete`
 * for Google) used to leave it behind, and `logoutUser` clears only `localStorage`.
 * A stale entry then won the next login, ahead of `state.from`, and sent the user to a
 * workspace they had asked for before a previous session.
 *
 * Exported rather than inlining `sessionStorage.removeItem` at the call site so the key
 * name stays private to the module that owns it.
 */
export const clearDeepLinkReturn = (): void => {
  try {
    sessionStorage.removeItem(WS_DEEP_LINK_KEY);
  } catch {
    // Private mode / storage unavailable — nothing to clear.
  }
};

/**
 * Retrieve (and clear) the stashed deep-link return path, or null when
 * absent / invalid.
 *
 * Clearing here covers the path that consumes it; `clearDeepLinkReturn` in `logoutUser`
 * covers every sign-in that completes without reading it.
 */
export const popDeepLinkReturn = (): string | null => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(WS_DEEP_LINK_KEY);
    sessionStorage.removeItem(WS_DEEP_LINK_KEY);
  } catch {
    return null;
  }
  if (!isAllowedDeepLinkPath(raw ?? "")) return null;
  return raw;
};
